/**
 * 操作日志页面
 */
import React, { useState, useEffect } from 'react';
import { Search, Clock, User, Activity, Calendar, Filter, Download, Eye, AlertTriangle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { api } from '../services/api';
import { OperationLogOut } from '../types/api';
import styles from './OperationLogs.module.css';

const OperationLogs: React.FC = () => {
  const [logs, setLogs] = useState<OperationLogOut[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<OperationLogOut[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOperationType, setSelectedOperationType] = useState('');
  const [selectedResourceType, setSelectedResourceType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 加载操作日志
  useEffect(() => {
    loadOperationLogs();
  }, []);

  // 过滤操作日志
  useEffect(() => {
    let result = logs;

    // 按搜索关键词过滤
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(log =>
        log.user_name.toLowerCase().includes(term) ||
        log.account.toLowerCase().includes(term) ||
        log.ip_address?.toLowerCase().includes(term) ||
        log.resource_name?.toLowerCase().includes(term) ||
        log.operation_content?.toLowerCase().includes(term) ||
        log.user_agent?.toLowerCase().includes(term)
      );
    }

    // 按用户过滤
    if (selectedUser) {
      result = result.filter(log => log.user_name === selectedUser);
    }

    // 按操作类型过滤
    if (selectedOperationType) {
      result = result.filter(log => log.operation_type === selectedOperationType);
    }

    // 按资源类型过滤
    if (selectedResourceType) {
      result = result.filter(log => log.resource_type === selectedResourceType);
    }

    setFilteredLogs(result);
  }, [searchTerm, selectedUser, selectedOperationType, selectedResourceType, logs]);

  const loadOperationLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // 检查是否有token
      const token = localStorage.getItem('access_token');
      if (!token) {
        setError('未登录，请先登录系统');
        setLoading(false);
        return;
      }

      console.log('开始加载操作日志，token存在:', !!token);
      const response = await api.getOperationLogs({ page: 1, page_size: 100 });
      console.log('操作日志响应:', response);
      if (response.data) {
        setLogs(response.data);
        setFilteredLogs(response.data);
      } else {
        throw new Error(response.message || '获取操作日志失败');
      }
    } catch (err) {
      console.error('加载操作日志失败:', err);
      let errorMessage = '获取操作日志失败';
      if (err instanceof Error) {
        errorMessage = err.message;
        // 检查是否是网络错误
        if (err.message === 'Failed to fetch') {
          errorMessage = '网络请求失败，请检查网络连接或后端服务是否运行';
        }
      }
      // 检查是否有更详细的错误信息
      if (typeof err === 'object' && err !== null) {
        console.error('错误详情:', JSON.stringify(err, null, 2));
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: number) => {
    if (status === 1) {
      return <CheckCircle size={16} className="success-icon" />;
    } else {
      return <AlertTriangle size={16} className="error-icon" />;
    }
  };

  const getOperationTypeColor = (type: string) => {
    switch (type) {
      case 'CREATE':
        return 'var(--green-500)';
      case 'UPDATE':
        return 'var(--blue-500)';
      case 'DELETE':
        return 'var(--red-500)';
      case 'VIEW':
        return 'var(--gray-500)';
      default:
        return 'var(--ink-500)';
    }
  };

  const getOperationTypeLabel = (type: string) => {
    switch (type) {
      case 'CREATE':
        return '新增';
      case 'UPDATE':
        return '编辑';
      case 'DELETE':
        return '删除';
      case 'VIEW':
        return '查看';
      default:
        return type;
    }
  };

  const getResourceTypeLabel = (type: string) => {
    switch (type) {
      case 'knowledge_archive':
        return '知识管理';
      case 'exam_paper':
        return '考试试卷';
      case 'learning_task':
        return '学习任务';
      case 'exam_task':
        return '考试任务';
      case 'user':
        return '用户';
      case 'role':
        return '角色';
      case 'department':
        return '部门';
      case 'tag':
        return '标签';
      case 'conversion_log':
        return '转换日志';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Clock className={`${styles.spin} ${styles.icon}`} size={32} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* 页头 */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.titleGroup}>
            <Activity size={24} className={styles.titleIcon} />
            <h1 className={styles.title}>操作日志</h1>
          </div>
          <p className={styles.subtitle}>系统操作行为审计与追踪</p>
        </div>
      </div>

      {/* 搜索和过滤工具栏 */}
      <div className={styles.toolbar}>
        <div className={styles.searchInput}>
          <Search size={18} strokeWidth={1.5} />
          <input
            type="text"
            placeholder="搜索用户、账号、IP、资源名称..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>操作类型:</label>
            <select
              value={selectedOperationType}
              onChange={(e) => setSelectedOperationType(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">全部</option>
              <option value="CREATE">新增</option>
              <option value="UPDATE">编辑</option>
              <option value="DELETE">删除</option>
              <option value="VIEW">查看</option>
            </select>
          </div>

          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>操作对象:</label>
            <select
              value={selectedResourceType}
              onChange={(e) => setSelectedResourceType(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="">全部</option>
              <option value="knowledge_archive">知识管理</option>
              <option value="exam_paper">考试试卷</option>
              <option value="learning_task">学习任务</option>
              <option value="exam_task">考试任务</option>
              <option value="user">用户</option>
              <option value="role">角色</option>
              <option value="department">部门</option>
              <option value="tag">标签</option>
              <option value="conversion_log">转换日志</option>
            </select>
          </div>

          <button className={styles.exportButton}>
            <Download size={16} />
            <span>导出</span>
          </button>
        </div>
      </div>

      {/* 日志列表 */}
      <div className={styles.logsContainer}>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.centerCell}>操作用户</th>
                <th className={styles.centerCell}>操作类型</th>
                <th className={styles.centerCell}>操作对象</th>
                <th className={styles.centerCell}>具体内容</th>
                <th className={styles.centerCell}>IP地址</th>
                <th className={styles.centerCell}>操作时间</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length > 0 ? (
                filteredLogs.map((log) => (
                  <motion.tr
                    key={log.log_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={styles.logRow}
                  >
                    <td className={`${styles.userCell} ${styles.centerCell}`}>
                      <div className={styles.userInfo}>
                        <User size={16} className={styles.userIcon} />
                        <div className={styles.userDetails}>
                          <div className={styles.userName}>{log.user_name}</div>
                          <div className={styles.userAccount}>{log.account}</div>
                        </div>
                      </div>
                    </td>
                    <td className={`${styles.typeCell} ${styles.centerCell}`}>
                      <span
                        className={styles.operationType}
                        style={{ color: getOperationTypeColor(log.operation_type) }}
                      >
                        {getOperationTypeLabel(log.operation_type)}
                      </span>
                    </td>
                    <td className={`${styles.typeCell} ${styles.centerCell}`}>
                      <span className={styles.resourceType}>
                        {getResourceTypeLabel(log.resource_type)}
                      </span>
                    </td>
                    <td className={`${styles.contentCell} ${styles.centerCell}`}>
                      <span className={styles.operationContent} title={log.operation_content || '-'}>
                        {log.operation_content || '-'}
                      </span>
                    </td>
                    <td className={`${styles.ipCell} ${styles.centerCell}`}>
                      <span className={styles.ipAddress}>{log.ip_address || '-'}</span>
                    </td>
                    <td className={`${styles.timeCell} ${styles.centerCell}`}>
                      <div className={styles.timeInfo}>
                        <Calendar size={14} className={styles.timeIcon} />
                        <span className={styles.timeText}>{formatDate(log.create_time)}</span>
                      </div>
                    </td>
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className={styles.centerCell}>
                    {error ? (
                      <div className={styles.errorMessage}>
                        {error}
                      </div>
                    ) : (
                      '暂无操作日志'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationLogs;